import {Injectable, OnDestroy} from '@angular/core';

import {Observable, ReplaySubject, Subject} from 'rxjs';
import {SmagApiService} from '../api/smag-api.service';
import {ConfigType, EconomicsConfig, TimeConfig} from 'smag-commons/cfg';
import {LoginService} from './login.service';
import {HierarchyService} from './hierarchy.service';
import {FeaturesConfig} from 'smag-commons/dto/api';

/**
 * Service that deals with generic configuration of SmartAggregator system.
 */
@Injectable()
export class ConfigurationService implements OnDestroy {

    private destroyed = new Subject<void>();
    private timeCfgSubj: ReplaySubject<TimeConfig>;
    private economicsCfgSubj: ReplaySubject<EconomicsConfig>;
    private featuresCfgSubj: ReplaySubject<FeaturesConfig>;

    constructor(private readonly apiService: SmagApiService,
                private readonly loginService: LoginService,
                private readonly hierarchyService: HierarchyService) {
        this.timeCfgSubj = new ReplaySubject<TimeConfig>(1); // like BehaviorSubject but with no need for initial value
        this.economicsCfgSubj = new ReplaySubject<EconomicsConfig>(1); // like BehaviorSubject but with no need for initial value
        this.featuresCfgSubj = new ReplaySubject<FeaturesConfig>(1); // like BehaviorSubject but with no need for initial value
        // reload is triggered every time a user gets logged-in (different principals may have different permissions)
        loginService.addSetupHook(1, profile => {
            // NOTE: hierarchy is actually not needed here, but we do not want to wait for completion
            // of network loading before setting up configurations
            console.debug(`Retrieving configurations for user ${profile?.username}`);
            return this.reloadConfigurations();
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get timeConfig(): Observable<TimeConfig> {
        return this.timeCfgSubj.asObservable();
    }

    get economicsConfig(): Observable<EconomicsConfig> {
        return this.economicsCfgSubj.asObservable();
    }

    get featuresConfig(): Observable<FeaturesConfig> {
        return this.featuresCfgSubj.asObservable();
    }

    private reloadTimeConfig(): Promise<TimeConfig> {
        return this.apiService.getConfig(ConfigType.TIME).toPromise()
            .then((cfg: TimeConfig) => {
                this.timeCfgSubj.next(cfg);
                return cfg;
            });
    }

    private reloadEconomicsConfig(): Promise<EconomicsConfig> {
        return this.apiService.getConfig(ConfigType.ECONOMICS).toPromise()
            .then((cfg: EconomicsConfig) => {
                this.economicsCfgSubj.next(cfg);
                return cfg;
            });
    }

    private reloadFeaturesConfig(): Promise<FeaturesConfig> {
        return this.apiService.getFeatureConfig().toPromise()
            .then((cfg: FeaturesConfig) => {
                this.featuresCfgSubj.next(cfg);
                return cfg;
            });
    }

    private reloadConfigurations(): Promise<void> {
        return Promise.all([
            this.reloadTimeConfig(), this.reloadEconomicsConfig(),
            this.reloadFeaturesConfig()
        ]).then();
    }
}
